import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Chip,
  FormControlLabel,
  Switch
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ReferenceArea, Area, ComposedChart } from 'recharts';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import moment from 'moment';

function BiometricActivity() {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/stations'; // Default to stations if no source provided
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [stationInfo, setStationInfo] = useState(null);
  const [showHourlyData, setShowHourlyData] = useState(true);
  const [showAllData, setShowAllData] = useState(false);

  // Fetch biometric data for the station
  const fetchBiometricData = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'activities', stationId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const activities = data.activities || [];

        // Calculate the time 8 hours ago from now
        const eightHoursAgo = moment().utc().subtract(8, 'hours');
        
        // Filter and process activities from the last 8 hours
        const filteredActivities = activities
          .filter(activity => {
            if (!activity.dateTime) return false;
            const activityTime = moment.utc(activity.dateTime);
            
            if (showHourlyData) {
              // For hourly data, only show activities at the top of the hour
              if (activityTime.local().format('mm') === '00') {
                return activityTime.isAfter(eightHoursAgo);
              }
              return false;
            } else {
              // For 15-minute increments, show activities at 00, 15, 30, 45 minutes
              const minutes = activityTime.local().format('mm');
              if (minutes === '00' || minutes === '15' || minutes === '30' || minutes === '45') {
                return activityTime.isAfter(eightHoursAgo);
              }
              return false;
            }
          })
          .map(activity => ({
            ...activity,
            // Divide biometric score by 2 as requested
            adjustedScore: Math.round((activity.score || 0) / 2),
            // Add pressure data for the new line (multiply by 100 for percentage)
            pressure: (activity.p || 0) * 100,
            // Add humidity data for stacked area (multiply by 100 for percentage)
            humidity: (activity.h || 0) * 100,
            // Add moon data for stacked area (multiply by 100 for percentage)
            moon: (activity.m || 0) * 100,
            // Add sun data for stacked area (multiply by 100 for percentage)
            sun: (activity.s || 0) * 100,
            // Add wind data for stacked area (multiply by 100 for percentage)
            wind: (activity.w || 0) * 100,
            // Format datetime for display in local time
            hour: moment.utc(activity.dateTime).local().format('HH:mm'),
            timestamp: moment.utc(activity.dateTime).valueOf()
          }))
          .sort((a, b) => a.timestamp - b.timestamp); // Sort by time

        setChartData(filteredActivities);
      } else {
        setError('No biometric data found for this station');
      }
    } catch (err) {
      console.error('Error fetching biometric data:', err);
      setError('Failed to load biometric data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (stationId) {
      fetchBiometricData();
    }
  }, [stationId, showHourlyData]);

  const handleBackClick = () => {
    navigate(from);
  };

  // Determine the back button label based on source
  const getBackButtonLabel = () => {
    return from === '/biometrics' ? 'Back to Biometrics' : 'Back to Stations';
  };

  // Function to determine activity level based on score
  const getActivityLevel = (score) => {
    if (score >= 0 && score <= 30) return { label: 'Sluggish', color: 'error' };
    if (score > 30 && score <= 60) return { label: 'Normal', color: 'warning' };
    if (score > 60 && score <= 90) return { label: 'Aggressive', color: 'success' };
    return { label: 'Unknown', color: 'default' };
  };

  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const activityLevel = getActivityLevel(data.adjustedScore);
      const fullDateTime = moment.utc(data.dateTime).local().format('YYYY-MM-DD HH:mm');
      
      return (
        <Paper sx={{ p: 2, border: '1px solid #ccc' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Date: {fullDateTime}
          </Typography>
          <Typography variant="body2" sx={{ color: '#2196f3' }}>
            Score: {data.adjustedScore}
          </Typography>
          <Typography variant="body2" sx={{ color: '#ff5722' }}>
            Pressure: {data.pressure.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#2196f3' }}>
            Humidity: {data.humidity.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#9c27b0' }}>
            Moon: {data.moon.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#ffc107' }}>
            Sun: {data.sun.toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ color: '#607d8b' }}>
            Wind: {data.wind.toFixed(1)}%
          </Typography>
          <Typography variant="body2" color={activityLevel.color === 'error' ? 'error.main' : activityLevel.color === 'success' ? 'success.main' : 'warning.main'}>
            Level: {activityLevel.label}
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={handleBackClick}
          sx={{ mb: 2, width: 'auto' }}
        >
          {getBackButtonLabel()}
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const currentTimestamp = moment().utc().valueOf();
  const currentScore = chartData.filter(c => c.timestamp <= currentTimestamp).slice(-1)[0]?.adjustedScore || 0;
  const currentLevel = getActivityLevel(currentScore);

  // Get current time for background shading (in local time)
  const currentTime = moment().utc().format('HH:00');
  console.log('Current Time:', currentTime);
  console.log('chartData:', chartData);
  
  // Find the latest data point that is before current time
  const pastDataEndIndex = chartData.findIndex(item => item.timestamp > currentTimestamp);
  const pastDataEnd = pastDataEndIndex > 0 ? chartData[pastDataEndIndex - 1]?.hour : null;
  
  return (
    <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 3 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={handleBackClick}
          sx={{ mb: 2 }}
        >
          {getBackButtonLabel()}
        </Button>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Typography variant="h6" color="text.secondary">
              Current Score: {currentScore}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Chip 
              label={`Current Level: ${currentLevel.label}`}
              color={currentLevel.color}
              variant="filled"
              sx={{ fontSize: '1rem', height: '32px' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={showHourlyData}
                  onChange={(e) => setShowHourlyData(e.target.checked)}
                  color="primary"
                />
              }
              label={showHourlyData ? "Hourly Data" : "15-Min Data"}
              sx={{ ml: 1 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={showAllData}
                  onChange={(e) => setShowAllData(e.target.checked)}
                  color="secondary"
                />
              }
              label="Show All Data"
              sx={{ ml: 1 }}
            />
          </Grid>
        </Grid>
      </Box>

      <Paper sx={{ p: 3, flexGrow: 1 }}>
        {chartData.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="400px">
            <Typography variant="h6" color="text.secondary">
              No biometric data available for the last 8 hours
            </Typography>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="hour"
                tick={{ fontSize: 12 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                yAxisId="left"
                domain={[0, 90]}
                ticks={[30, 60]}
                tick={{ fontSize: 12 }}
                label={{ value: 'Biometric Score', angle: -90, position: 'insideLeft' }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                label={{ value: 'Percent of Score %', angle: 90, position: 'insideRight' }}
                hide={!showAllData}
              />
              <Tooltip content={<CustomTooltip />} />
              
              {/* Watermark background bands */}
              <ReferenceArea yAxisId="left" y1={0} y2={30} fill="#f44336" fillOpacity={0.05} label={{ value: 'Sluggish', position: 'insideLeft', fill: '#f44336', fontSize: 28, fontWeight: 'bold', opacity: 0.3 }} />
              <ReferenceArea yAxisId="left" y1={30} y2={60} fill="#ff9800" fillOpacity={0.05} label={{ value: 'Normal', position: 'insideLeft', fill: '#ff9800', fontSize: 28, fontWeight: 'bold', opacity: 0.3 }} />
              <ReferenceArea yAxisId="left" y1={60} y2={90} fill="#4caf50" fillOpacity={0.05} label={{ value: 'Aggressive', position: 'insideLeft', fill: '#4caf50', fontSize: 28, fontWeight: 'bold', opacity: 0.3 }} />

              {/* Reference lines for activity levels */}
              <ReferenceLine yAxisId="left" y={30} stroke="#f44336" strokeDasharray="5 5" />
              <ReferenceLine yAxisId="left" y={60} stroke="#ff9800" strokeDasharray="5 5" />
              
              {/* Current time line */}
              <ReferenceLine 
                x={currentTime}
                stroke="#000000" 
                strokeWidth={10} 
                strokeDasharray="none"
                label={{ value: "Current Time", position: "top", fill: "#000000", fontSize: 12, fontWeight: "bold" }}
              />
              
              {showAllData && (
                <>
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="pressure" 
                    stackId="1"
                    stroke="#ff5722" 
                    fill="#ff5722"
                    fillOpacity={0.1}
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="humidity" 
                    stackId="1"
                    stroke="#2196f3" 
                    fill="#2196f3"
                    fillOpacity={0.1}
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="moon" 
                    stackId="1"
                    stroke="#9c27b0" 
                    fill="#9c27b0"
                    fillOpacity={0.1}
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="sun" 
                    stackId="1"
                    stroke="#ffc107" 
                    fill="#ffc107"
                    fillOpacity={0.1}
                  />
                  <Area 
                    yAxisId="right"
                    type="monotone" 
                    dataKey="wind" 
                    stackId="1"
                    stroke="#607d8b" 
                    fill="#607d8b"
                    fillOpacity={0.1}
                  />
                </>
              )}
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="adjustedScore" 
                stroke="#2196f3" 
                strokeWidth={4}
                dot={{ fill: '#2196f3', strokeWidth: 4, r: 4 }}
                activeDot={{ r: 6, stroke: '#2196f3', strokeWidth: 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Legend */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#2196f3' }} />
          <Typography variant="caption">Biometric Score</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#ff5722' }} />
          <Typography variant="caption">Pressure %</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#2196f3' }} />
          <Typography variant="caption">Humidity %</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#9c27b0' }} />
          <Typography variant="caption">Moon %</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#ffc107' }} />
          <Typography variant="caption">Sun %</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#607d8b' }} />
          <Typography variant="caption">Wind %</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#f44336' }} />
          <Typography variant="caption">Sluggish (0-30)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#ff9800' }} />
          <Typography variant="caption">Normal (30-60)</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: '#4caf50' }} />
          <Typography variant="caption">Aggressive (60-90)</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default BiometricActivity;
